import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export interface AdminUser {
  id: string;
  first_name: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  plan: string | null;
  role: string | null;
  created_at: string | null;
  is_subscribed: boolean | null;
  email_confirmed: boolean | null;
  billing_period: string | null;
  subscription_end_date: string | null;
  last_payment_date: string | null;
  last_payment_status: string | null;
  last_payment_error: string | null;
  stripe_customer_id: string | null;
  subscription_id: string | null;
  updated_at: string;
  // Novos campos de histórico de ativações
  first_activation_date: string | null;
  last_activation_date: string | null;
  activation_count: number | null;
}

export const useAdminData = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const syncAllEmailConfirmationStatus = async () => {
    try {
      console.log('Iniciando sincronização de email_confirmed usando SQL function...');
      
      // Usar uma função SQL para sincronizar todos os status de email confirmado
      const { error } = await (supabase as any).rpc('sync_all_email_confirmation_status');
      
      if (error) {
        console.error('Erro ao sincronizar status de email confirmado:', error);
        // Se a função não existir, criar uma atualização manual
        console.log('Tentando sincronização manual...');
        
        // Buscar todos os perfis e verificar o status de confirmação individualmente
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, email_confirmed');
          
        if (profilesError) {
          console.error('Erro ao buscar profiles:', profilesError);
          return;
        }
        
        console.log(`Sincronizando ${profiles.length} profiles individualmente...`);
        
        // Para cada profile, verificar se precisa de sincronização
        for (const profile of profiles) {
          try {
            // Usar a função individual de confirmação que já existe
            await supabase.rpc('admin_confirm_user_email', {
              user_id_to_confirm: profile.id
            });
          } catch (err) {
            console.log(`Erro ao sincronizar perfil ${profile.id}:`, err);
          }
        }
      } else {
        console.log('Sincronização SQL executada com sucesso');
      }
    } catch (error) {
      console.error('Erro na sincronização completa:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      
      console.log('Buscando usuários do profiles...');
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar usuários:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os usuários.",
          variant: "destructive",
        });
        return;
      }

      console.log('Dados dos usuários recebidos:', data);
      
      // Verificar status de email_confirmed
      data?.forEach(user => {
        console.log(`Usuário ${user.email}: Email Confirmed = ${user.email_confirmed}`);
      });

      setUsers((data || []) as AdminUser[]);
    } catch (err) {
      console.error('Erro na busca de usuários:', err);
      toast({
        title: "Erro",
        description: "Erro interno ao carregar usuários.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const confirmUserEmail = async (userId: string) => {
    try {
      console.log(`Confirmando email do usuário: ${userId}`);
      
      // Usar a função admin para confirmar o email
      const { error: adminError } = await supabase.rpc('admin_confirm_user_email', {
        user_id_to_confirm: userId
      });

      if (adminError) {
        console.error('Erro na função admin:', adminError);
        
        // Fallback: atualização direta no perfil
        const { error: directError } = await supabase
          .from('profiles')
          .update({ 
            email_confirmed: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', userId);
          
        if (directError) {
          throw directError;
        }
        
        console.log('Email confirmado via atualização direta');
      } else {
        console.log('Email confirmado via função admin');
      }
      
      toast({
        title: "Sucesso",
        description: "Email do usuário confirmado com sucesso!",
      });
      
      // Aguardar um pouco e recarregar os dados
      setTimeout(() => {
        fetchUsers();
      }, 500);
      
    } catch (error) {
      console.error("Erro ao confirmar email:", error);
      toast({
        title: "Erro",
        description: "Erro ao confirmar email do usuário",
        variant: "destructive",
      });
    }
  };

  const updateUserPlan = async (userId: string, newPlan: string) => {
    try {
      // Calculate subscription details based on plan
      const isSubscribed = newPlan !== 'free';
      const now = new Date();
      const endDate = new Date();
      
      // Define plan durations
      let billingPeriod = 'monthly';
      if (newPlan === 'quarterly') {
        endDate.setMonth(endDate.getMonth() + 3);
        billingPeriod = 'quarterly';
      } else if (newPlan === 'annual') {
        endDate.setFullYear(endDate.getFullYear() + 1);
        billingPeriod = 'annual';
      } else if (newPlan === 'basic') {
        endDate.setDate(endDate.getDate() + 15); // 15 days for basic
        billingPeriod = 'trial';
      } else if (newPlan === 'vip') {
        endDate.setMonth(endDate.getMonth() + 6); // 6 months for VIP
        billingPeriod = 'biannual';
      } else if (newPlan !== 'free') {
        endDate.setMonth(endDate.getMonth() + 1); // Default 1 month for premium
      }

      // Buscar dados atuais do usuário para verificar primeira ativação
      const { data: currentUser } = await supabase
        .from('profiles')
        .select('first_activation_date, activation_count')
        .eq('id', userId)
        .single();

      const updateData: any = {
        plan: newPlan,
        is_subscribed: isSubscribed,
        updated_at: now.toISOString(),
        last_activation_date: isSubscribed ? now.toISOString() : null,
        activation_count: isSubscribed 
          ? (currentUser?.activation_count || 0) + 1 
          : currentUser?.activation_count || 0
      };

      // Se é a primeira ativação, registrar a data
      if (isSubscribed && !currentUser?.first_activation_date) {
        updateData.first_activation_date = now.toISOString();
      }

      if (isSubscribed) {
        updateData.billing_period = billingPeriod;
        updateData.subscription_end_date = endDate.toISOString();
        updateData.last_payment_date = now.toISOString();
        updateData.last_payment_status = 'succeeded';
      } else {
        updateData.billing_period = null;
        updateData.subscription_end_date = null;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId);

      if (error) {
        console.error('Erro ao atualizar plano:', error);
        toast({
          title: "Erro",
          description: `Não foi possível atualizar o plano: ${error.message}`,
          variant: "destructive",
        });
        return false;
      }

      // Registrar no histórico de ativações
      if (isSubscribed) {
        const { data: authUser } = await supabase.auth.getUser();
        
        await supabase.from('plan_activations').insert({
          user_id: userId,
          plan: newPlan,
          billing_period: billingPeriod,
          activation_date: now.toISOString(),
          expiration_date: endDate.toISOString(),
          activated_by: authUser?.user?.id || null,
          activation_type: 'manual',
          notes: `Plano ${newPlan} ativado manualmente pelo admin`
        });
      }

      await fetchUsers(); // Refresh data
      toast({
        title: "Sucesso",
        description: `Plano do usuário atualizado para ${newPlan} com sucesso.`,
      });
      return true;
    } catch (err) {
      console.error('Erro ao atualizar plano:', err);
      return false;
    }
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          role: newRole,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Erro ao atualizar role:', error);
        toast({
          title: "Erro",
          description: `Não foi possível atualizar o papel: ${error.message}`,
          variant: "destructive",
        });
        return false;
      }

      await fetchUsers(); // Refresh data
      toast({
        title: "Sucesso",
        description: `Papel do usuário atualizado para ${newRole}.`,
      });
      return true;
    } catch (err) {
      console.error('Erro ao atualizar role:', err);
      return false;
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      // Usar a função SQL que deleta completamente (perfil + auth)
      const { data, error } = await (supabase as any)
        .rpc('delete_user_completely', {
          user_id_to_delete: userId
        });

      if (error) {
        console.error('Erro ao deletar usuário:', error);
        toast({
          title: "Erro",
          description: `Não foi possível deletar o usuário: ${error.message}`,
          variant: "destructive",
        });
        return false;
      }

      if (!data) {
        toast({
          title: "Erro",
          description: "Falha ao deletar usuário. Verifique as permissões.",
          variant: "destructive",
        });
        return false;
      }

      await fetchUsers(); // Refresh data
      toast({
        title: "Sucesso",
        description: "Usuário deletado completamente do sistema.",
      });
      return true;
    } catch (err) {
      console.error('Erro ao deletar usuário:', err);
      return false;
    }
  };

  // Função especial para ativar a usuária Marineia
  const activateMarineia = async () => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          plan: 'premium',
          is_subscribed: true,
          billing_period: 'monthly',
          subscription_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 dias
          last_payment_date: new Date().toISOString(),
          last_payment_status: 'succeeded',
          updated_at: new Date().toISOString()
        })
        .eq('email', 'marineiapereira148@gmail.com');

      if (error) {
        console.error('Erro ao ativar Marineia:', error);
        return false;
      }

      toast({
        title: "Sucesso",
        description: "Usuária Marineia ativada com sucesso!",
      });
      await fetchUsers();
      return true;
    } catch (err) {
      console.error('Erro ao ativar Marineia:', err);
      return false;
    }
  };

  useEffect(() => {
    fetchUsers();

    // Ativar Marineia automaticamente na primeira carga
    const activateMarineiaOnLoad = async () => {
      try {
        const { data: marineia } = await supabase
          .from('profiles')
          .select('*')
          .eq('email', 'marineiapereira148@gmail.com')
          .single();

        if (marineia && !marineia.is_subscribed) {
          console.log('Ativando Marineia automaticamente...');
          await activateMarineia();
        }
      } catch (err) {
        console.log('Marineia não encontrada ou já ativa');
      }
    };

    activateMarineiaOnLoad();

    // Set up real-time subscription for automatic updates
    const channel = supabase
      .channel('profiles-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles'
        },
        (payload) => {
          console.log('Profile updated via realtime:', payload);
          // Refresh users list when any profile changes
          fetchUsers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    users,
    isLoading,
    fetchUsers,
    confirmUserEmail,
    updateUserPlan,
    updateUserRole,
    deleteUser,
    activateMarineia
  };
};
